/* MapLibre GL JS premium map with Benchory custom style + interactive layers.

Free, no-key alternative to Mapbox using CartoCDN raster tiles
(Positron / Dark / Voyager). 100% free, attribution included.
*/
import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

const TILE_STYLES = {
  positron: {
    name: 'Clair (Positron)',
    style: {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: [
            'https://basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
            'https://b.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
            'https://c.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
          ],
          tileSize: 256,
          attribution:
            '&copy; OpenStreetMap contributors &copy; CARTO',
        },
      },
      layers: [{ id: 'raster-tiles', type: 'raster', source: 'raster-tiles' }],
    },
  },
  voyager: {
    name: 'Color (Voyager)',
    style: {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: [
            'https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
            'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
            'https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
          ],
          tileSize: 256,
          attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
        },
      },
      layers: [{ id: 'raster-tiles', type: 'raster', source: 'raster-tiles' }],
    },
  },
  dark: {
    name: 'Sombre',
    style: {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: [
            'https://basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
            'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
            'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
          ],
          tileSize: 256,
          attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
        },
      },
      layers: [{ id: 'raster-tiles', type: 'raster', source: 'raster-tiles' }],
    },
  },
};

const TYPE_VISUALS = {
  pharmacie:    { color: '#DC2626', icon: '+',  size: 30, priority: 100 },
  hopital:      { color: '#7F1D1D', icon: 'H', size: 26, priority: 90 },
  ehpad:        { color: '#7C3AED', icon: 'E',  size: 26, priority: 80 },
  msp:          { color: '#0EA5E9', icon: 'M',  size: 22, priority: 70 },
  laboratoire:  { color: '#06B6D4', icon: 'L',  size: 18, priority: 60 },
  medecin_gen:  { color: '#10B981', icon: 'G',  size: 18, priority: 60 },
  medecin_spe:  { color: '#10B981', icon: 'S',  size: 18, priority: 55 },
  dentiste:     { color: '#0891B2', icon: 'D',  size: 16, priority: 50 },
  infirmier:    { color: '#F59E0B', icon: 'I',  size: 14, priority: 40 },
  kine:         { color: '#F97316', icon: 'K',  size: 14, priority: 40 },
  ecole:        { color: '#3B82F6', icon: 'E',  size: 18, priority: 30 },
  creche:       { color: '#60A5FA', icon: 'C',  size: 14, priority: 25 },
  college:      { color: '#2563EB', icon: 'C',  size: 16, priority: 28 },
  universite:   { color: '#1D4ED8', icon: 'U',  size: 18, priority: 32 },
  entreprise:   { color: '#64748B', icon: 'E',  size: 14, priority: 20 },
  coworking:    { color: '#94A3B8', icon: 'W',  size: 14, priority: 18 },
  administration: { color: '#475569', icon: 'A', size: 14, priority: 16 },
  supermarche:  { color: '#84CC16', icon: 'S',  size: 14, priority: 12 },
  centre_commercial: { color: '#A3E635', icon: 'M', size: 16, priority: 13 },
  banque:       { color: '#16A34A', icon: 'B',  size: 12, priority: 10 },
  poste:        { color: '#FACC15', icon: 'P',  size: 12, priority: 10 },
  salle_sport:  { color: '#FB923C', icon: 'S',  size: 12, priority: 8 },
  centre_sportif: { color: '#FDBA74', icon: 'C', size: 14, priority: 8 },
  gare:         { color: '#7C3AED', icon: 'G',  size: 18, priority: 35 },
  bus_stop:     { color: '#A78BFA', icon: 'B',  size: 8,  priority: 5 },
  velo:         { color: '#8B5CF6', icon: 'V',  size: 6,  priority: 3 },
  clinique:     { color: '#BE185D', icon: 'C',  size: 22, priority: 75 },
};

export default function MapLibreView({
  center,
  radius,
  establishments,
  activeLayers,
  onSelectEstablishment,
  styleKey = 'voyager',
  showHeatmap = null,
  heatPoints = [],
  fullscreen = false,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  // Init map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: TILE_STYLES[styleKey].style,
      center: [center.lng, center.lat],
      zoom: 13.5,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');
    map.addControl(new maplibregl.ScaleControl({ maxWidth: 100, unit: 'metric' }), 'bottom-left');
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line
  }, []);

  // Switch style
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setStyle(TILE_STYLES[styleKey].style);
    map.once('styledata', () => updateMarkers());
    // eslint-disable-next-line
  }, [styleKey]);

  // Center/radius update
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !center) return;
    map.flyTo({ center: [center.lng, center.lat], zoom: 13.5, duration: 600 });
  }, [center]);

  // Pharmacy center marker + radius circle
  const pharmacyMarkerRef = useRef(null);
  const radiusSourceAddedRef = useRef(false);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !center) return;
    const onLoad = () => {
      // Pharmacy center marker
      if (pharmacyMarkerRef.current) pharmacyMarkerRef.current.remove();
      const el = document.createElement('div');
      el.style.cssText = `
        width: 44px; height: 44px; border-radius: 50%;
        background: linear-gradient(135deg, #1B2A4A, #2C3E5C);
        border: 4px solid #B8860B;
        box-shadow: 0 4px 16px rgba(0,0,0,0.3), 0 0 0 8px rgba(184,134,11,0.18);
        display: flex; align-items: center; justify-content: center;
        color: #B8860B; font-weight: 900; font-size: 14px;
        cursor: pointer; transform: translateY(-2px);
      `;
      el.textContent = '\u2605';
      pharmacyMarkerRef.current = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([center.lng, center.lat])
        .addTo(map);

      // Radius polygon
      try {
        if (map.getLayer('radius-fill')) map.removeLayer('radius-fill');
        if (map.getLayer('radius-line')) map.removeLayer('radius-line');
        if (map.getSource('radius')) map.removeSource('radius');
      } catch (e) { /* ignore */ }
      const ring = generateCircleGeoJson(center.lng, center.lat, radius);
      map.addSource('radius', { type: 'geojson', data: ring });
      map.addLayer({
        id: 'radius-fill', type: 'fill', source: 'radius',
        paint: { 'fill-color': '#1B2A4A', 'fill-opacity': 0.05 },
      });
      map.addLayer({
        id: 'radius-line', type: 'line', source: 'radius',
        paint: { 'line-color': '#1B2A4A', 'line-width': 2, 'line-dasharray': [3, 2], 'line-opacity': 0.5 },
      });
      radiusSourceAddedRef.current = true;
    };
    if (map.loaded()) onLoad(); else map.once('load', onLoad);
    // eslint-disable-next-line
  }, [center, radius]);

  // Markers update
  const markersRef = useRef([]);

  const updateMarkers = () => {
    const map = mapRef.current;
    if (!map) return;
    // Remove old markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    // Filter by active layers
    const filtered = (establishments || []).filter(e => activeLayers.has(e.type));
    // Sort by priority so important ones are on top
    filtered.sort((a, b) => (TYPE_VISUALS[a.type]?.priority || 0) - (TYPE_VISUALS[b.type]?.priority || 0));

    filtered.forEach(e => {
      const [lng, lat] = e.location?.coordinates || [0, 0];
      if (!lat || !lng) return;
      const v = TYPE_VISUALS[e.type] || { color: '#888', icon: '?', size: 14 };
      const el = document.createElement('div');
      el.style.cssText = `
        width: ${v.size}px; height: ${v.size}px; border-radius: 50%;
        background: ${v.color}; border: 2px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.25);
        cursor: pointer; transition: transform 200ms, box-shadow 200ms;
        display: flex; align-items: center; justify-content: center;
        color: white; font-weight: 900; font-size: ${Math.max(8, v.size * 0.45)}px;
      `;
      if (v.size >= 14) el.textContent = v.icon;
      el.setAttribute('data-testid', `marker-${e.id}`);
      el.onmouseenter = () => {
        el.style.transform = 'scale(1.4)';
        el.style.boxShadow = `0 4px 14px ${v.color}88`;
      };
      el.onmouseleave = () => {
        el.style.transform = 'scale(1)';
        el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.25)';
      };
      el.onclick = () => onSelectEstablishment?.(e);

      const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([lng, lat])
        .addTo(map);
      markersRef.current.push(marker);
    });
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (map.loaded()) updateMarkers();
    else map.once('load', updateMarkers);
    // eslint-disable-next-line
  }, [establishments, activeLayers]);

  // Heatmap
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      try {
        if (map.getLayer('heatmap-layer')) map.removeLayer('heatmap-layer');
        if (map.getSource('heatmap-src')) map.removeSource('heatmap-src');
      } catch (e) { /* ignore */ }
      if (!showHeatmap || !heatPoints.length) return;
      const features = heatPoints.map(p => ({
        type: 'Feature',
        properties: { intensity: p[2] },
        geometry: { type: 'Point', coordinates: [p[1], p[0]] },
      }));
      map.addSource('heatmap-src', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features },
      });
      const colorRamps = {
        seniors:    ['interpolate', ['linear'], ['heatmap-density'], 0, 'rgba(0,0,0,0)', 0.2, '#10B981', 0.5, '#F59E0B', 0.85, '#DC2626', 1, '#7F1D1D'],
        medical:    ['interpolate', ['linear'], ['heatmap-density'], 0, 'rgba(0,0,0,0)', 0.2, '#DC2626', 0.5, '#F59E0B', 0.85, '#22C55E', 1, '#10B981'],
        commercial: ['interpolate', ['linear'], ['heatmap-density'], 0, 'rgba(0,0,0,0)', 0.2, '#94A3B8', 0.5, '#3B82F6', 0.85, '#B8860B', 1, '#DC2626'],
      };
      map.addLayer({
        id: 'heatmap-layer',
        type: 'heatmap',
        source: 'heatmap-src',
        maxzoom: 17,
        paint: {
          'heatmap-weight': ['interpolate', ['linear'], ['get', 'intensity'], 0, 0, 100, 1],
          'heatmap-intensity': 0.7,
          'heatmap-radius': 35,
          'heatmap-opacity': 0.7,
          'heatmap-color': colorRamps[showHeatmap] || colorRamps.seniors,
        },
      });
    };
    if (map.loaded()) apply(); else map.once('load', apply);
  }, [showHeatmap, heatPoints]);

  // Fullscreen resize
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    setTimeout(() => map.resize(), 350);
  }, [fullscreen]);

  return <div ref={containerRef} className="w-full h-full" data-testid="maplibre-container" />;
}

function generateCircleGeoJson(lng, lat, radiusMeters, points = 64) {
  const coords = [];
  const radiusInDeg = radiusMeters / 111000;
  for (let i = 0; i < points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const dx = (radiusInDeg / Math.cos((lat * Math.PI) / 180)) * Math.cos(angle);
    const dy = radiusInDeg * Math.sin(angle);
    coords.push([lng + dx, lat + dy]);
  }
  coords.push(coords[0]);
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Polygon', coordinates: [coords] },
  };
}

export { TYPE_VISUALS, TILE_STYLES };
